/**
 * Detection + drop helpers for the browser's opaque cross-origin "Script error."
 *
 * When a script served from a different origin (a third-party SDK, a CDN bundle,
 * or a script injected by a browser extension / content blocker) throws an
 * uncaught error, the browser masks it for privacy: `window.onerror` receives
 * only the literal message "Script error." with no file, line, or stack trace.
 * PostHog and Sentry both surface this via their global handlers as a synthetic,
 * stack-less exception. It is unactionable noise (the real error is unknowable)
 * and non-blocking for users, so we drop it before ingestion in both SDKs.
 *
 * Pure + provider-agnostic so it can be unit-tested without the SDK runtimes.
 */

export interface NormalizedException {
  /** The exception message/value, e.g. "Script error." */
  value?: string | null;
  /** Number of resolved stack frames. Absent/0 means the error is opaque. */
  frameCount?: number;
}

const SCRIPT_ERROR_RE = /^script error\.?$/i;

/**
 * True when a single exception is the opaque cross-origin "Script error."
 * variant: the canonical message AND no stack frames. A "Script error." that
 * carries real frames is NOT opaque (it's a genuine error) and returns false.
 */
export function isOpaqueScriptException(e: NormalizedException): boolean {
  const value = (e.value ?? '').trim();
  return SCRIPT_ERROR_RE.test(value) && !e.frameCount;
}

/**
 * True only when EVERY exception in the chain is the opaque "Script error."
 * variant. Any chain that also holds a genuine error is kept.
 */
export function isOpaqueScriptError(exceptions: NormalizedException[] | undefined): boolean {
  if (!exceptions || exceptions.length === 0) return false;
  return exceptions.every(isOpaqueScriptException);
}
